import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@context/AuthContext";
import axios from "axios";
import { API_BASE, PROFILE_IMG_BASE } from "@config/env";


const post = (ep, f) => { const fd=new FormData(); Object.entries(f).forEach(([k,v])=>{ if(v!=null) fd.append(k,v); }); return axios.post(`${API_BASE}${ep}`,fd).then(r=>r.data).catch(()=>null); };

const JOB_TYPES = [{ value:"1",label:"Full-time" },{ value:"2",label:"Part-time" },{ value:"3",label:"Contract" },{ value:"4",label:"Permanent" }];

function Field({ label, required, error, children }) {
  return (
    <div style={{ marginBottom:16 }}>
      <label style={{ fontSize:11, fontWeight:700, color:"var(--fc-text-secondary)", display:"block", marginBottom:5, textTransform:"uppercase", letterSpacing:"0.05em" }}>
        {label}{required && " *"}
      </label>
      {children}
      {error && <div style={{ fontSize:11, color:"#DC2626", marginTop:3 }}>{error}</div>}
    </div>
  );
}

const inp = (value, onChange, placeholder, type="text", extra={}) => (
  <input type={type} value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder} {...extra}
    style={{ width:"100%", boxSizing:"border-box", padding:"10px 12px", border:"1.5px solid var(--fc-border)", borderRadius:9, fontSize:13, outline:"none", fontFamily:"inherit", color:"var(--fc-text-primary)", background:"var(--fc-card-bg)", transition:"all 0.18s" }}
    onFocus={e=>{e.target.style.borderColor="#2563EB";e.target.style.boxShadow="0 0 0 3px rgba(79,70,229,0.1)"}}
    onBlur={e=>{e.target.style.borderColor="var(--fc-border)";e.target.style.boxShadow="none"}} />
);

const textarea = (value, onChange, placeholder, rows=4) => (
  <textarea value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder} rows={rows}
    style={{ width:"100%", boxSizing:"border-box", padding:"10px 12px", border:"1.5px solid var(--fc-border)", borderRadius:9, fontSize:13, outline:"none", fontFamily:"inherit", color:"var(--fc-text-primary)", background:"var(--fc-card-bg)", resize:"vertical", transition:"all 0.18s" }}
    onFocus={e=>{e.target.style.borderColor="#2563EB"}} onBlur={e=>{e.target.style.borderColor="var(--fc-border)"}} />
);

export default function CreateJobPage() {
  const { userSlug, user } = useAuth();
  const navigate = useNavigate();

  const [mode,     setMode]     = useState("manual"); // "manual" | "file" | "content"
  const [firmList, setFirmList] = useState([]);
  const [selFirms, setSelFirms] = useState([]);
  const [busy,     setBusy]     = useState(false);
  const [success,  setSuccess]  = useState("");
  const [error,    setError]    = useState("");
  const [errors,   setErrors]   = useState({});

  // Form fields
  const [form, setForm] = useState({
    jobTitle:"", description:"", skills:"", experience:"",
    jobType:"1", contractLength:"", compensation:"", locationReq:"",
    other:"", employerName:"", employerDesc:"", benefits:"",
    workingHours:"", closingDate:"", jobRequirements:"",
    applicationDetails:"", employerContact:"", employerWebsite:"",
  });
  const set = (k, v) => { setForm(f=>({...f,[k]:v})); setErrors(e=>({...e,[k]:""})); };

  // File upload
  const [file,      setFile]      = useState(null);
  const [fileName,  setFileName]  = useState("");
  const [content,   setContent]   = useState("");
  const [parsing,   setParsing]   = useState(false);
  const [parsed,    setParsed]    = useState(null);

  // Load firm access list
  useEffect(() => {
    if (!userSlug) return;
    post("get-all-firm-access-list", { user_slug: userSlug }).then(data => {
      const list = data?.firmList || data?.data?.firmList || [];
      setFirmList(Array.isArray(list) ? list : []);
      setSelFirms((Array.isArray(list) ? list : []).map(f => f.firm_id));
    });
  }, [userSlug]);

  const toggleFirm = (id) => {
    setSelFirms(s => s.includes(id) ? s.filter(x=>x!==id) : [...s, id]);
  };

  const validate = () => {
    const e = {};
    if (!form.jobTitle.trim()) e.jobTitle = "Job title is required";
    if (!form.description.trim()) e.description = "Description is required";
    setErrors(e);
    return !Object.keys(e).length;
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setBusy(true); setError("");
    const res = await post("create-job", {
      user_slug: userSlug, job_slug: "",
      job_title: form.jobTitle, job_description: form.description,
      firm_ids: selFirms.join(","),
      required_skill_set: form.skills, experience_required: form.experience,
      job_type: form.jobType, contract_length: form.contractLength,
      compensation: form.compensation, location_requirement: form.locationReq,
      other: form.other, employer_name: form.employerName,
      employer_description: form.employerDesc, benefits: form.benefits,
      working_hours: form.workingHours, closing_date: form.closingDate,
      job_requirements: form.jobRequirements, application_details: form.applicationDetails,
      employer_contact: form.employerContact, employer_website: form.employerWebsite,
    });
    setBusy(false);
    if (res?.status === 1) setSuccess(res.message || "Job posted successfully!");
    else setError(res?.message || "Failed to create job. Please try again.");
  };

  const handleFileUpload = async () => {
    if (!file) return;
    setParsing(true);
    const fd = new FormData();
    fd.append("user_slug", userSlug);
    fd.append("file", file);
    const res = await axios.post(`${API_BASE}create-job-via-parsing`, fd).then(r=>r.data).catch(()=>null);
    setParsing(false);
    if (res?.status === 1) { setSuccess("Job created from file successfully!"); }
    else setError(res?.message || "Failed to parse file.");
  };

  const handleContentSubmit = async () => {
    if (!content.trim()) { setError("Please paste some content"); return; }
    setBusy(true); setError("");
    const res = await post("create-job-via-content", { user_slug: userSlug, content });
    setBusy(false);
    if (res?.status === 1) setSuccess("Job created from content successfully!");
    else setError(res?.message || "Failed to create job from content.");
  };

  if (success) {
    return (
      <div style={{ background:"var(--fc-card-bg)", border:"1px solid var(--fc-border)", borderRadius:16, padding:"48px 24px", textAlign:"center", maxWidth:500, margin:"0 auto" }}>
        <div style={{ width:52, height:52, borderRadius:14, background:"var(--fc-green-light)", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 16px" }}>
          <i className="fa-solid fa-circle-check" style={{ fontSize:22, color:"var(--fc-green)" }} />
        </div>
        <h2 style={{ fontSize:18, fontWeight:700, color:"var(--fc-text-primary)", marginBottom:8 }}>Job Posted!</h2>
        <p style={{ fontSize:13, color:"var(--fc-text-muted)", marginBottom:24 }}>{success}</p>
        <div style={{ display:"flex", gap:8, justifyContent:"center" }}>
          <button onClick={()=>setSuccess("")} className="fc-btn fc-btn-sm fc-btn-ghost fc-btn-pill">Post another</button>
          <button onClick={()=>navigate("/jobs")} className="fc-btn fc-btn-sm fc-btn-primary fc-btn-pill">View all jobs</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom:20 }}>
        <button onClick={()=>navigate("/jobs")} style={{ background:"none", border:"none", cursor:"pointer", color:"var(--fc-text-muted)", fontSize:13, padding:0, display:"flex", alignItems:"center", gap:6, marginBottom:8, fontFamily:"inherit" }}>
          <i className="fa-solid fa-arrow-left" style={{fontSize:11}} /> Back to jobs
        </button>
        <h1 style={{ fontSize:20, fontWeight:800, color:"var(--fc-text-primary)", margin:0 }}>Create Job</h1>
        <p style={{ fontSize:13, color:"var(--fc-text-muted)", margin:"3px 0 0" }}>Post a new job opening</p>
      </div>

      {/* Mode selector */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:8, marginBottom:20 }}>
        {[
          { key:"manual",  label:"Manual Entry",   icon:"fa-pen-to-square", desc:"Fill in all details manually" },
          { key:"file",    label:"Upload File",     icon:"fa-file-arrow-up", desc:"Parse job from a document" },
          { key:"content", label:"Paste Content",   icon:"fa-paste",         desc:"Paste job description text" },
        ].map(m => (
          <button key={m.key} onClick={() => setMode(m.key)}
            style={{ padding:"14px 12px", borderRadius:12, border:`2px solid ${mode===m.key?"#2563EB":"var(--fc-border)"}`, background:mode===m.key?"var(--fc-primary-light)":"var(--fc-card-bg)", cursor:"pointer", fontFamily:"inherit", textAlign:"center", transition:"all 0.15s" }}>
            <i className={`fa-solid ${m.icon}`} style={{ fontSize:20, color:mode===m.key?"#2563EB":"var(--fc-text-subtle)", display:"block", marginBottom:6 }} />
            <div style={{ fontSize:12, fontWeight:700, color:mode===m.key?"#2563EB":"var(--fc-text-secondary)" }}>{m.label}</div>
            <div style={{ fontSize:11, color:"var(--fc-text-subtle)", marginTop:2 }}>{m.desc}</div>
          </button>
        ))}
      </div>

      {error && (
        <div style={{ padding:"12px 14px", background:"var(--fc-red-light)", border:"1px solid var(--fc-red-border)", borderRadius:10, marginBottom:16, fontSize:13, color:"#DC2626" }}>
          <i className="fa-solid fa-circle-exclamation" style={{marginRight:8}} />{error}
        </div>
      )}

      {/* File upload mode */}
      {mode === "file" && (
        <div style={{ background:"var(--fc-card-bg)", border:"1px solid var(--fc-border)", borderRadius:16, padding:24 }}>
          <div style={{ border:"2px dashed var(--fc-border)", borderRadius:12, padding:"32px 24px", textAlign:"center", cursor:"pointer", transition:"all 0.15s",
            ...(fileName ? { borderColor:"#2563EB", background:"var(--fc-primary-light)" } : {}) }}
            onDragOver={e=>{e.preventDefault();e.currentTarget.style.borderColor="#2563EB";}}
            onDragLeave={e=>{e.currentTarget.style.borderColor=fileName?"#2563EB":"var(--fc-border)";}}
            onDrop={e=>{e.preventDefault();const f=e.dataTransfer.files[0];if(f){setFile(f);setFileName(f.name);}}}
            onClick={()=>document.getElementById("job-file-input").click()}>
            <i className="fa-solid fa-file-arrow-up" style={{ fontSize:28, color:fileName?"#2563EB":"var(--fc-text-hint)", display:"block", marginBottom:10 }} />
            {fileName ? (
              <><div style={{ fontSize:14, fontWeight:700, color:"#2563EB" }}>{fileName}</div><div style={{ fontSize:12, color:"var(--fc-text-subtle)", marginTop:4 }}>Click to change file</div></>
            ) : (
              <><div style={{ fontSize:14, fontWeight:600, color:"var(--fc-text-secondary)" }}>Drop your file here or click to browse</div><div style={{ fontSize:12, color:"var(--fc-text-subtle)", marginTop:4 }}>PDF, DOCX, TXT supported</div></>
            )}
          </div>
          <input id="job-file-input" type="file" accept=".pdf,.doc,.docx,.txt" style={{ display:"none" }}
            onChange={e=>{const f=e.target.files[0];if(f){setFile(f);setFileName(f.name);}}} />
          <button onClick={handleFileUpload} disabled={!file||parsing} className="fc-btn fc-btn-md fc-btn-primary fc-btn-pill" style={{ marginTop:16, width:"100%" }}>
            {parsing ? <><i className="fa-solid fa-spinner fa-spin" style={{fontSize:12}} /> Parsing & Creating...</> : "Create from File"}
          </button>
        </div>
      )}

      {/* Content paste mode */}
      {mode === "content" && (
        <div style={{ background:"var(--fc-card-bg)", border:"1px solid var(--fc-border)", borderRadius:16, padding:24 }}>
          <Field label="Paste Job Description">
            {textarea(content, setContent, "Paste the full job description here...", 12)}
          </Field>
          <button onClick={handleContentSubmit} disabled={busy||!content.trim()} className="fc-btn fc-btn-md fc-btn-primary fc-btn-pill" style={{ width:"100%" }}>
            {busy ? <><i className="fa-solid fa-spinner fa-spin" style={{fontSize:12}} /> Creating...</> : "Create from Content"}
          </button>
        </div>
      )}

      {/* Manual entry mode */}
      {mode === "manual" && (
        <form onSubmit={handleManualSubmit}>
          <div style={{ background:"var(--fc-card-bg)", border:"1px solid var(--fc-border)", borderRadius:16, padding:24, marginBottom:14 }}>
            <h2 style={{ fontSize:14, fontWeight:700, color:"var(--fc-text-primary)", marginBottom:16, paddingBottom:10, borderBottom:"1px solid var(--fc-border-light)" }}>Basic Information</h2>
            <Field label="Job Title" required error={errors.jobTitle}>
              {inp(form.jobTitle, v=>set("jobTitle",v), "e.g. Senior React Developer")}
            </Field>
            <Field label="Job Description" required error={errors.description}>
              {textarea(form.description, v=>set("description",v), "Describe the role, responsibilities and what you're looking for...", 6)}
            </Field>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
              <Field label="Skills Required">
                {inp(form.skills, v=>set("skills",v), "e.g. React, TypeScript, Node.js")}
              </Field>
              <Field label="Experience Required">
                {inp(form.experience, v=>set("experience",v), "e.g. 5+ years")}
              </Field>
            </div>
            <Field label="Job Type">
              <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                {JOB_TYPES.map(t => (
                  <button key={t.value} type="button" onClick={() => set("jobType",t.value)}
                    style={{ padding:"7px 16px", borderRadius:9, border:`1.5px solid ${form.jobType===t.value?"#2563EB":"var(--fc-border)"}`, background:form.jobType===t.value?"var(--fc-primary-light)":"var(--fc-card-bg)", color:form.jobType===t.value?"#2563EB":"var(--fc-text-muted)", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
                    {t.label}
                  </button>
                ))}
              </div>
            </Field>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
              <Field label="Compensation">{inp(form.compensation, v=>set("compensation",v), "e.g. $80,000 - $100,000")}</Field>
              <Field label="Location" error={errors.locationReq}>{inp(form.locationReq, v=>set("locationReq",v), "e.g. Remote, New York, USA or Remote")}</Field>
              <Field label="Contract Length">{inp(form.contractLength, v=>set("contractLength",v), "e.g. 6 months")}</Field>
              <Field label="Closing Date">{inp(form.closingDate, v=>set("closingDate",v), "", "date")}</Field>
              <Field label="Working Hours">{inp(form.workingHours, v=>set("workingHours",v), "e.g. Mon-Fri 9-5")}</Field>
              <Field label="Benefits">{inp(form.benefits, v=>set("benefits",v), "e.g. Health, Dental, RRSP")}</Field>
            </div>
          </div>

          <div style={{ background:"var(--fc-card-bg)", border:"1px solid var(--fc-border)", borderRadius:16, padding:24, marginBottom:14 }}>
            <h2 style={{ fontSize:14, fontWeight:700, color:"var(--fc-text-primary)", marginBottom:16, paddingBottom:10, borderBottom:"1px solid var(--fc-border-light)" }}>Additional Details</h2>
            <Field label="Job Requirements">
              {textarea(form.jobRequirements, v=>set("jobRequirements",v), "Specific requirements or qualifications...")}
            </Field>
            <Field label="Application Details">
              {textarea(form.applicationDetails, v=>set("applicationDetails",v), "How to apply, what to include...", 3)}
            </Field>
            <Field label="Other Requirements">
              {textarea(form.other, v=>set("other",v), "Any other details...", 3)}
            </Field>
          </div>

          {/* Employer details */}
          <div style={{ background:"var(--fc-card-bg)", border:"1px solid var(--fc-border)", borderRadius:16, padding:24, marginBottom:14 }}>
            <h2 style={{ fontSize:14, fontWeight:700, color:"var(--fc-text-primary)", marginBottom:16, paddingBottom:10, borderBottom:"1px solid var(--fc-border-light)" }}>Employer Details (optional)</h2>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
              <Field label="Employer Name">{inp(form.employerName, v=>set("employerName",v), "Company name")}</Field>
              <Field label="Contact">{inp(form.employerContact, v=>set("employerContact",v), "Contact details")}</Field>
              <Field label="Website">{inp(form.employerWebsite, v=>set("employerWebsite",v), "https://")}</Field>
            </div>
            <Field label="About Employer">
              {textarea(form.employerDesc, v=>set("employerDesc",v), "Brief description of the employer...", 3)}
            </Field>
          </div>

          {/* Firm access */}
          {firmList.length > 0 && (
            <div style={{ background:"var(--fc-card-bg)", border:"1px solid var(--fc-border)", borderRadius:16, padding:24, marginBottom:14 }}>
              <h2 style={{ fontSize:14, fontWeight:700, color:"var(--fc-text-primary)", marginBottom:4, paddingBottom:10, borderBottom:"1px solid var(--fc-border-light)" }}>Firm Access</h2>
              <p style={{ fontSize:12, color:"var(--fc-text-muted)", marginBottom:14 }}>Select which firms can see this job posting</p>
              <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
                {firmList.map(f => (
                  <button key={f.firm_id} type="button" onClick={() => toggleFirm(f.firm_id)}
                    style={{ display:"flex", alignItems:"center", gap:7, padding:"6px 12px", borderRadius:9, border:`1.5px solid ${selFirms.includes(f.firm_id)?"#2563EB":"var(--fc-border)"}`, background:selFirms.includes(f.firm_id)?"var(--fc-primary-light)":"var(--fc-card-bg)", cursor:"pointer", fontFamily:"inherit", fontSize:12, fontWeight:600, color:selFirms.includes(f.firm_id)?"#2563EB":"var(--fc-text-muted)", transition:"all 0.15s" }}>
                    <div style={{ width:16, height:16, borderRadius:4, border:`2px solid ${selFirms.includes(f.firm_id)?"#2563EB":"var(--fc-text-hint)"}`, background:selFirms.includes(f.firm_id)?"#2563EB":"transparent", display:"flex", alignItems:"center", justifyContent:"center" }}>
                      {selFirms.includes(f.firm_id) && <i className="fa-solid fa-check" style={{fontSize:8,color:"#fff"}} />}
                    </div>
                    {f.firm_name}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
            <button type="button" onClick={()=>navigate("/jobs")} className="fc-btn fc-btn-md fc-btn-ghost fc-btn-pill">Cancel</button>
            <button type="submit" disabled={busy} className="fc-btn fc-btn-md fc-btn-primary fc-btn-pill" style={{ minWidth:130 }}>
              {busy ? <><i className="fa-solid fa-spinner fa-spin" style={{fontSize:12}} /> Creating...</> : <><i className="fa-solid fa-plus" style={{fontSize:11}} /> Create Job</>}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
